import { useState, type ReactElement } from 'react';
import { ActionIcon, Badge, Box, Button, Divider, Group, Popover, Stack, Text, UnstyledButton } from '@mantine/core';
import { ChevronLeft, Filter, X } from 'lucide-react';
import type { AnalyticsField } from '@/shared/analytics/AnalyticsTypes';
import { LocalFieldChecklist, PersonSearchChecklist, ProviderConnectionChecklist } from './FilterFieldEditors';

export interface ActiveFilterEntry {
    field: string;
    /** Human label for the field ("Provider", "Department", …). */
    label: string;
    /** True when the field belongs to the persistent global tier. */
    global: boolean;
    values: string[];
}

interface ActiveFiltersChipProps {
    entries: ActiveFilterEntry[];
    valueLabel: (field: string, value: string) => string;
    valueIcon: (field: string, value: string) => ReactElement | null;
    onToggle: (entry: ActiveFilterEntry, value: string, checked: boolean) => void;
    onClearAll: () => void;
}

/**
 * The compact "Filters (n)" summary chip that replaces per-value pills. Its
 * popover lists every active filter with per-value removal and a Clear all;
 * picking a field switches to that field's checklist for in-place editing.
 */
export default function ActiveFiltersChip({ entries, valueLabel, valueIcon, onToggle, onClearAll }: ActiveFiltersChipProps): ReactElement {
    const [opened, setOpened] = useState(false);
    const [editField, setEditField] = useState<string | null>(null);

    const close = (): void => {
        setOpened(false);
        setEditField(null);
    };
    const count = entries.reduce((total, entry) => total + entry.values.length, 0);
    const editEntry = entries.find((entry) => entry.field === editField) ?? null;

    const editorFor = (entry: ActiveFilterEntry): ReactElement => {
        const toggle = (value: string, checked: boolean): void => onToggle(entry, value, checked);
        if (entry.field === 'provider_connection_id') return <ProviderConnectionChecklist selected={entry.values} onToggle={toggle} />;
        if (entry.field === 'user_id') return <PersonSearchChecklist selected={entry.values} onToggle={toggle} />;
        return <LocalFieldChecklist field={entry.field as AnalyticsField} selected={entry.values} onToggle={toggle} />;
    };

    return (
        <Popover opened={opened} onChange={(next) => (next ? setOpened(true) : close())} position="bottom-start" width={320} shadow="md" radius="md" withinPortal trapFocus>
            <Popover.Target>
                <Button
                    size="sm"
                    radius="md"
                    variant="default"
                    fw={500}
                    leftSection={<Filter size={15} />}
                    rightSection={
                        <Badge size="sm" variant="light" px={6}>
                            {count}
                        </Badge>
                    }
                    onClick={() => (opened ? close() : setOpened(true))}
                >
                    Filters
                </Button>
            </Popover.Target>
            <Popover.Dropdown>
                {editEntry !== null ? (
                    <Stack gap="xs">
                        <Group gap={4} wrap="nowrap">
                            <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Back to active filters" onClick={() => setEditField(null)}>
                                <ChevronLeft size={14} />
                            </ActionIcon>
                            <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
                                {editEntry.label}
                            </Text>
                        </Group>
                        {editorFor(editEntry)}
                    </Stack>
                ) : (
                    <Stack gap="sm">
                        {entries.map((entry) => (
                            <Box key={entry.field}>
                                <Group justify="space-between" mb={4} wrap="nowrap">
                                    <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
                                        {entry.label}
                                    </Text>
                                    <UnstyledButton onClick={() => setEditField(entry.field)}>
                                        <Text size="xs" c="var(--mantine-primary-color-filled)" fw={500}>
                                            Edit
                                        </Text>
                                    </UnstyledButton>
                                </Group>
                                <Stack gap={4}>
                                    {entry.values.map((value) => (
                                        <Group key={value} gap={8} wrap="nowrap" justify="space-between">
                                            <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                                                {valueIcon(entry.field, value)}
                                                <Text size="sm" truncate>
                                                    {valueLabel(entry.field, value)}
                                                </Text>
                                            </Group>
                                            <ActionIcon
                                                variant="subtle"
                                                color="gray"
                                                size="xs"
                                                aria-label={`Remove ${entry.label} ${valueLabel(entry.field, value)}`}
                                                onClick={() => onToggle(entry, value, false)}
                                            >
                                                <X size={12} />
                                            </ActionIcon>
                                        </Group>
                                    ))}
                                </Stack>
                            </Box>
                        ))}
                        <Divider />
                        <Button
                            variant="subtle"
                            color="gray"
                            size="compact-sm"
                            onClick={() => {
                                onClearAll();
                                close();
                            }}
                        >
                            Clear all
                        </Button>
                    </Stack>
                )}
            </Popover.Dropdown>
        </Popover>
    );
}
